import { useAuth } from './useAuth';

/**
 * Hook para verificar se o usuário autenticado possui uma role específica
 * 
 * @param role - Role a ser verificada (master, diretor, gerente, etc.)
 * @returns boolean - true se o usuário possui a role
 * 
 * @example
 * const isMaster = useHasRole('master');
 * {isMaster && <Button>Forçar Apagar</Button>}
 */
export const useHasRole = (role: string): boolean => {
  const { profile } = useAuth();
  
  if (!profile?.roles) {
    return false;
  }
  
  return profile.roles.includes(role);
};

/**
 * Hook para verificar se o usuário possui qualquer uma das roles listadas
 * 
 * @param roles - Array de roles a serem verificadas
 * @returns boolean - true se o usuário possui pelo menos uma das roles
 * 
 * @example
 * const isGestor = useHasAnyRole(['master', 'diretor', 'gerente']);
 */
export const useHasAnyRole = (roles: string[]): boolean => {
  const { profile } = useAuth();
  
  if (!profile?.roles) {
    return false;
  }
  
  return roles.some(role => profile.roles.includes(role));
};

/**
 * Hook para verificar se o usuário possui todas as roles listadas
 * 
 * @param roles - Array de roles a serem verificadas
 * @returns boolean - true se o usuário possui todas as roles
 */
export const useHasAllRoles = (roles: string[]): boolean => {
  const { profile } = useAuth();
  
  if (!profile?.roles) {
    return false;
  }
  
  return roles.every(role => profile.roles.includes(role));
};
